import React, { useState, useEffect, useCallback } from "react";
import InnerLayout from "../../../layouts/inner-layout";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import DatePickerInput from "../../../forms/date-picker";
import ItemsSelectFieldStandalone from "../../../forms/stand-alone-select-field";
import { api } from "../../../../redux/apis/api";
import successToast from "../../../helper-components/success-toast";
import errorToast from "../../../helper-components/error-toast";
import {
    getBulkSyncState,
    formatDashboardCountdown,
    setSyncFrozenNow,
    getSyncFrozenUntil,
    DASHBOARD_SYNC_FREEZE_DURATION_MS,
} from "../../../helper-components/sync-freeze";
import { isAdmin } from "../../../../redux/config";

const getToday = () => new Date().toLocaleDateString("sv-SE");

const POS_SYNC_MODULES = [
    { key: "billing", apiType: "billing", name: "Billing", mode: "dateRange", endpoint: "/order/hard_sync" },
    { key: "return_bill", apiType: "return billing", name: "Return Bill", mode: "dateRange", endpoint: "/return-order/hard-sync" },
    { key: "purchase_order", apiType: "purchase order", name: "Purchase Order", mode: "dateRange", endpoint: "/purchaseorder/hard-sync-intercmy-and-normal" },
    { key: "purchase_invoice", apiType: "purchase invoice", name: "Purchase Invoice", mode: "dateRange", endpoint: "/purchaseinvoice/hard-sync-normal" },
    { key: "purchase_return", apiType: "purchase return", name: "Purchase Return", mode: "dateRange", endpoint: "/purchasereturn/hard-sync-intercmy-and-normal" },
    { key: "purchase_credit_note", apiType: "purchase credit note", name: "Purchase Credit Note", mode: "dateRange", endpoint: "/purchasecreditnote/hard-sync-intercmy-and-normal" },
    { key: "customer", apiType: "customer", name: "Customer", mode: "dateRange", endpoint: "/customer/hard-sync-customer" },
    { key: "new_items", apiType: "new items", name: "New Items", mode: "instant", endpoint: "/item/hard_sync_new_items" },
];

const CPOS_SYNC_MODULES = [
    { key: "delivery_note", apiType: "delivery note", name: "Delivery Note", mode: "dateRange", endpoint: "/salesorder/hard-sync-delivery-note" },
    { key: "sales_invoice", apiType: "sales invoice", name: "Sales Invoice", mode: "dateRange", endpoint: "/salesorder/hard-sync-sales-invoice" },
    { key: "store_info", apiType: "store info", name: "Store info", mode: "multiSelect", paramKey: "store_id", endpoint: "/store/hard-sync-store-information" },
    { key: "pricelist", apiType: "pricelist", name: "Pricelist", mode: "multiSelect", paramKey: "price_list_id", endpoint: "/pricelist/hard-sync-price-list" },
];

const SYNC_MODULES = isAdmin ? CPOS_SYNC_MODULES : POS_SYNC_MODULES;

const SyncDashboard = () => {
    const navigate = useNavigate();
    const today = getToday();

    const [rows, setRows] = useState(
        SYNC_MODULES.map((mod) => ({
            ...mod,
            fromDate: "",
            toDate: "",
            selectedIds: [],
            syncing: false,
            lastSynced: null,
        }))
    );

    const [priceListOptions, setPriceListOptions] = useState([]);
    const [storeOptions, setStoreOptions] = useState([]);

    // Pricelist and Store info rows (CPOS only) sync a chosen set of records
    // instead of a date range, so their options are fetched up front.
    useEffect(() => {
        if (!isAdmin) return;

        api.get("/discount/pricelist/dropdown")
            .then((res) => {
                setPriceListOptions(
                    (res?.data?.data ?? []).map((item) => ({
                        label: item?.name ?? item?.price_list_name,
                        value: item?.id ?? item?.price_list_id,
                    }))
                );
            })
            .catch(() => {});

        api.get("/store/hard-sync-store-dropdown")
            .then((res) => {
                setStoreOptions(
                    (res?.data?.data ?? []).map((item) => ({
                        label: item?.store_code ?? item?.name ?? item?.concated_store_name,
                        value: item?.store_id ?? item?.id,
                    }))
                );
            })
            .catch(() => {});
    }, []);

    const updateRow = (key, patch) => {
        setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    };

    // multiSelect rows (Store info, Pricelist) freeze individual ids client-side once
    // synced, instead of blocking the whole module — so unrelated stores/pricelists
    // stay syncable while the just-synced ones cool down.
    const getOptionRemainingMs = (rowKey, id) => {
        const remainingMs = getSyncFrozenUntil(`sync_dashboard_${rowKey}_${id}`) - Date.now();
        return remainingMs > 0 ? remainingMs : 0;
    };

    // Pulls last_synced per module type from the backend; this (with a 1 hour cooldown)
    // is the source of truth for whether each module's Sync button is enabled.
    const fetchTimings = useCallback(async () => {
        try {
            const res = await api.get("/sync-dashboard/timings");
            const timings = res?.data?.data ?? [];
            setRows((prev) =>
                prev.map((row) => {
                    const match = timings.find((t) => t?.type === row.apiType);
                    return match ? { ...row, lastSynced: match.last_synced ?? null } : row;
                })
            );
        } catch (error) {
            // ignore; buttons fall back to enabled if timings are unavailable
        }
    }, []);

    useEffect(() => {
        fetchTimings();
    }, [fetchTimings]);

    // Tick every second so the countdown updates and the button re-enables automatically
    // once the 1 hour cooldown passes, without needing a manual refresh.
    useEffect(() => {
        const interval = setInterval(() => setRows((prev) => [...prev]), 1000);
        return () => clearInterval(interval);
    }, []);

    const handleSync = async (key) => {
        const row = rows.find((r) => r.key === key);

        if (row.mode === "multiSelect") {
            if (!row.selectedIds || row.selectedIds.length === 0) {
                errorToast(`Please select at least one ${row.name.toLowerCase()}`);
                return;
            }
        } else if (row.mode === "dateRange" && (!row.fromDate || !row.toDate)) {
            errorToast("Please select both From and To dates");
            return;
        }

        // multiSelect rows never gate on the module-level timing — frozen ids are
        // already excluded from selection, so any selection here is syncable.
        const syncState = getBulkSyncState({
            moduleKey: `sync_dashboard_${key}`,
            lastSynced: row.lastSynced,
        });
        if (row.syncing || (row.mode !== "multiSelect" && syncState.disabled)) return;

        updateRow(key, { syncing: true });

        try {
            if (row.mode === "multiSelect") {
                await api.post(row.endpoint, { [row.paramKey]: row.selectedIds });
                row.selectedIds.forEach((id) =>
                    setSyncFrozenNow(`sync_dashboard_${key}_${id}`, DASHBOARD_SYNC_FREEZE_DURATION_MS)
                );
                updateRow(key, { selectedIds: [] });
            } else if (row.mode === "dateRange") {
                await api.post(row.endpoint, {}, {
                    params: { from_date: row.fromDate, to_date: row.toDate },
                });
                // Freeze locally right away so the button can't be re-triggered while the
                // refreshed server timing (fetchTimings) is still in flight or fails.
                setSyncFrozenNow(`sync_dashboard_${key}`, DASHBOARD_SYNC_FREEZE_DURATION_MS);
            } else {
                await api.post(row.endpoint, {});
                setSyncFrozenNow(`sync_dashboard_${key}`, DASHBOARD_SYNC_FREEZE_DURATION_MS);
            }
            successToast(`${row.name} synced successfully`);
            fetchTimings();
        } catch (error) {
            errorToast(error.response?.data?.message || `Failed to sync ${row.name}`);
        } finally {
            updateRow(key, { syncing: false });
        }
    };

    const tableHeading = ["Module", "From Date", "To Date", "Last Synced", "Action"];

    return (
        <InnerLayout list={true} title="Sync Dashboard" module="sync-dashboard">
            <div className="flex flex-col h-full bg-white relative">
                {/* Header Breadcrumb */}
                <div className="flex justify-between items-center pt-5 mx-6 mb-6">
                    <div className="flex items-center gap-2 text-sml">
                        <button
                            className="flex items-center gap-2"
                            onClick={() => {
                                navigate("/settings");
                            }}
                        >
                            <ChevronLeftIcon className="h-5 w-5 inline-block" />
                            System Settings /
                        </button>
                        <span className="cursor-pointer">{" " + "Sync Dashboard"}</span>
                    </div>
                </div>

                {/* Table Container */}
                <div className="mx-6 flex-1 overflow-hidden pb-4 flex flex-col">
                    <div className="overflow-auto rounded-xl border border-[var(--list-table-border-color)] bg-white flex-1 custom-scrollbar shadow-sm">
                        <table className="w-full text-left border-collapse min-w-max">
                            <thead className="sticky top-0 bg-[var(--list-table-heading-bg)] z-[10]">
                                <tr className="relative h-[50px]">
                                    {tableHeading.map((heading) => (
                                        <th
                                            key={heading}
                                            className="px-5 py-3 text-[var(--list-table-heading-fg-color)] text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] uppercase tracking-wider whitespace-nowrap border-b border-[var(--list-table-border-color)]"
                                        >
                                            {heading}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--list-table-border-color)]">
                                {rows.map((row) => {
                                    const syncState = getBulkSyncState({
                                        moduleKey: `sync_dashboard_${row.key}`,
                                        lastSynced: row.lastSynced,
                                    });
                                    const isMultiSelect = row.mode === "multiSelect";
                                    const rawOptions = row.key === "pricelist" ? priceListOptions : storeOptions;
                                    const enrichedOptions = isMultiSelect
                                        ? rawOptions.map((option) => {
                                              const remainingMs = getOptionRemainingMs(row.key, option.value);
                                              return remainingMs > 0
                                                  ? {
                                                        ...option,
                                                        isDisabled: true,
                                                        label: `${option.label} (Synced • ${formatDashboardCountdown(remainingMs)})`,
                                                    }
                                                  : option;
                                          })
                                        : rawOptions;
                                    const actionDisabled = row.syncing || (isMultiSelect
                                        ? !(row.selectedIds && row.selectedIds.length > 0)
                                        : syncState.disabled);
                                    return (
                                        <tr
                                            key={row.key}
                                            className="transition-all duration-200 h-[56px] group bg-[var(--list-table-row-bg)] hover:bg-[var(--list-table-row-hover-bg)]"
                                        >
                                            <td className={`px-5 py-3 whitespace-nowrap border-b border-[var(--list-table-border-color)] font-medium text-sm text-gray-800 ${row.mode === "multiSelect" ? "min-w-[320px]" : "min-w-[180px]"}`}>
                                                {row.mode === "multiSelect" ? (
                                                    <div className="flex flex-col gap-1.5 w-[320px] py-1 whitespace-normal">
                                                        <span>{row.name}</span>
                                                        <ItemsSelectFieldStandalone
                                                            value={row.selectedIds}
                                                            options={enrichedOptions}
                                                            onChange={(selected) => updateRow(row.key, { selectedIds: (selected || []).map((o) => o.value) })}
                                                            placeholder={row.key === "pricelist" ? "Select Price List" : "Select Store"}
                                                            isSearchable={true}
                                                            isMulti={true}
                                                        />
                                                    </div>
                                                ) : (
                                                    row.name
                                                )}
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap border-b border-[var(--list-table-border-color)]">
                                                {row.mode === "dateRange" ? (
                                                    <DatePickerInput
                                                        value={row.fromDate}
                                                        onChange={(val) => updateRow(row.key, { fromDate: val })}
                                                        placeholder="From Date"
                                                        maxDate={row.toDate && row.toDate < today ? row.toDate : today}
                                                        rangeRole="start"
                                                        rangeEnd={row.toDate}
                                                    />
                                                ) : (
                                                    <span className="text-gray-400">--</span>
                                                )}
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap border-b border-[var(--list-table-border-color)]">
                                                {row.mode === "dateRange" ? (
                                                    <DatePickerInput
                                                        value={row.toDate}
                                                        onChange={(val) => updateRow(row.key, { toDate: val })}
                                                        placeholder="To Date"
                                                        minDate={row.fromDate}
                                                        rangeRole="end"
                                                        rangeStart={row.fromDate}
                                                        maxDate={today}
                                                    />
                                                ) : (
                                                    <span className="text-gray-400">--</span>
                                                )}
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap border-b border-[var(--list-table-border-color)] text-sm text-gray-500">
                                                {syncState.lastSyncedLabel}
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap border-b border-[var(--list-table-border-color)]">
                                                <button
                                                    className="bg-[var(--primary-button-bg-color)] text-white px-4 py-1.5 rounded-md text-[11px] font-semibold flex items-center gap-1.5 hover:bg-blue-600 shadow-sm transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed min-w-[92px] justify-center"
                                                    onClick={() => handleSync(row.key)}
                                                    disabled={actionDisabled}
                                                    title={!isMultiSelect && syncState.disabled ? "This module was recently synced. Please wait before syncing again." : ""}
                                                >
                                                    <RefreshCw
                                                        size={12}
                                                        strokeWidth={2.5}
                                                        className={row.syncing ? "animate-spin" : ""}
                                                    />
                                                    {row.syncing
                                                        ? "Syncing..."
                                                        : !isMultiSelect && syncState.disabled
                                                            ? formatDashboardCountdown(syncState.remainingMs)
                                                            : "Sync"}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </InnerLayout>
    );
};

export default SyncDashboard;
